// Segurança do condomínio (P-14).
//
// A aba era HTML puro: três `<select>` sem `value`, dois `<input type="date">`
// sem estado e um "Salvar" sem `onClick`. Não recebia nem o `id` do condomínio,
// então não havia como gravar mesmo que alguém tentasse.
//
// O AVCB saiu das perguntas: é `tmpl-001` em `alvaras_condominio`, com número,
// vencimento e anexo. A aba mostra o registro e leva para onde ele se edita, em
// vez de guardar um segundo sim/não que pode discordar do primeiro.

import { useCallback, useEffect, useState } from 'react';
import {
  lerAlvaras,
  lerSeguranca,
  gravarSeguranca,
  SEGURANCA_VAZIA,
  TEMPLATE_AVCB,
  type AlvaraRegistro,
  type SegurancaDoCondominio,
} from '@/lib/condominioRepo';
import { useJanelaDeAlerta } from '@/lib/notificacoes';
import { hojeISO } from '@/lib/datas';
import DocumentoDoAlvara from '@/pages/condominios/detail/components/DocumentoDoAlvara';

const CLASSE_CAMPO =
  'w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm';

/** Nulo é "ainda não respondido", e é diferente de "não". */
function SimNao({
  id,
  rotulo,
  valor,
  onChange,
}: {
  id: string;
  rotulo: string;
  valor: boolean | null;
  onChange: (v: boolean | null) => void;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-foreground-700 mb-1.5">
        {rotulo}
      </label>
      <select
        id={id}
        value={valor === null ? '' : valor ? 'sim' : 'nao'}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value === 'sim')}
        className={CLASSE_CAMPO}
      >
        <option value="">Selecione...</option>
        <option value="sim">Sim</option>
        <option value="nao">Não</option>
      </select>
    </div>
  );
}

export default function SegurancaTab({ condoId }: { condoId: string }) {
  const janelaAlvara = useJanelaDeAlerta('alvara');

  const [dados, setDados] = useState<SegurancaDoCondominio>(SEGURANCA_VAZIA);
  const [avcb, setAvcb] = useState<AlvaraRegistro | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState<{ texto: string; tipo: 'ok' | 'erro' } | null>(null);

  const carregar = useCallback(async () => {
    if (!condoId) return;
    setCarregando(true);
    try {
      const [seguranca, alvaras] = await Promise.all([
        lerSeguranca(condoId),
        lerAlvaras(condoId, janelaAlvara),
      ]);
      setDados(seguranca ?? SEGURANCA_VAZIA);
      setAvcb(alvaras.find((a) => a.templateId === TEMPLATE_AVCB) ?? null);
      setAviso(null);
    } catch (e: any) {
      setAviso({ texto: e?.message || 'Erro ao carregar.', tipo: 'erro' });
    } finally {
      setCarregando(false);
    }
  }, [condoId, janelaAlvara]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const alterar = <K extends keyof SegurancaDoCondominio>(
    campo: K,
    valor: SegurancaDoCondominio[K],
  ) => {
    setDados((atual) => ({ ...atual, [campo]: valor }));
    setAviso(null);
  };

  const salvar = async () => {
    setSalvando(true);
    setAviso(null);
    try {
      // Campo de data vazio chega como string vazia e o banco recusa: `date` não
      // aceita `''`. Nulo é a ausência.
      await gravarSeguranca(condoId, {
        ...dados,
        brigada_ultimo_treinamento: dados.brigada_ultimo_treinamento || null,
        extintores_ultima_recarga: dados.extintores_ultima_recarga || null,
        mangueiras_ultima_manutencao: dados.mangueiras_ultima_manutencao || null,
        projeto_tecnico_observacao: dados.projeto_tecnico_observacao?.trim() || null,
        observacoes: dados.observacoes?.trim() || null,
      });
      setAviso({ texto: 'Segurança salva.', tipo: 'ok' });
    } catch (e: any) {
      setAviso({ texto: e?.message || 'Não foi possível salvar.', tipo: 'erro' });
    } finally {
      setSalvando(false);
    }
  };

  if (carregando) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex items-center gap-3 text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-xl"></i>
          <span className="text-sm">Carregando segurança...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* AVCB — mostrado, não perguntado */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
        <h3 className="text-sm font-semibold text-foreground-900 mb-4">Corpo de Bombeiros</h3>
        <DocumentoDoAlvara
          titulo="AVCB — Auto de Vistoria do Corpo de Bombeiros"
          explicacao="O AVCB é um alvará: número, emissão, vencimento e documento digitalizado ficam no registro dele."
          registro={avcb}
          condoId={condoId}
        />
      </div>

      {/* Projeto técnico */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
        <h3 className="text-sm font-semibold text-foreground-900 mb-4">Projeto Técnico</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SimNao
            id="seg-projeto"
            rotulo="Possui Projeto Técnico aprovado?"
            valor={dados.possui_projeto_tecnico}
            onChange={(v) => alterar('possui_projeto_tecnico', v)}
          />
          <div>
            <label
              htmlFor="seg-projeto-obs"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              Observação
              <span className="text-foreground-400 font-normal ml-1">(opcional)</span>
            </label>
            <input
              id="seg-projeto-obs"
              type="text"
              value={dados.projeto_tecnico_observacao ?? ''}
              onChange={(e) => alterar('projeto_tecnico_observacao', e.target.value)}
              placeholder="Número do processo, data de aprovação..."
              className={CLASSE_CAMPO}
            />
          </div>
        </div>
        <p className="text-xs text-foreground-500 mt-3">
          O questionário de bombeiros exige projeto técnico aprovado para edificação acima de 750
          m². A resposta aqui é a declaração do condomínio sobre esse requisito.
        </p>
      </div>

      {/* Brigada */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
        <h3 className="text-sm font-semibold text-foreground-900 mb-4">Brigada de Incêndio</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SimNao
            id="seg-brigada"
            rotulo="Treinamento da Brigada efetuado?"
            valor={dados.brigada_treinada}
            onChange={(v) => alterar('brigada_treinada', v)}
          />
          <div>
            <label
              htmlFor="seg-brigada-data"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              Data do último treinamento
            </label>
            <input
              id="seg-brigada-data"
              type="date"
              max={hojeISO()}
              value={dados.brigada_ultimo_treinamento ?? ''}
              onChange={(e) => alterar('brigada_ultimo_treinamento', e.target.value || null)}
              className={CLASSE_CAMPO}
            />
          </div>
        </div>
        <p className="text-xs text-foreground-500 mt-3">
          Esta é a declaração do condomínio. Quem fez o treinamento, pessoa a pessoa e com
          vencimento, fica na aba Funcionários.
        </p>
      </div>

      {/* Extintores e mangueiras */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
        <h3 className="text-sm font-semibold text-foreground-900 mb-4">Extintores e Mangueiras</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="seg-extintores"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              Data da última recarga (Extintores)
            </label>
            <input
              id="seg-extintores"
              type="date"
              max={hojeISO()}
              value={dados.extintores_ultima_recarga ?? ''}
              onChange={(e) => alterar('extintores_ultima_recarga', e.target.value || null)}
              className={CLASSE_CAMPO}
            />
          </div>
          <div>
            <label
              htmlFor="seg-mangueiras"
              className="block text-sm font-medium text-foreground-700 mb-1.5"
            >
              Data da última manutenção (Mangueiras)
            </label>
            <input
              id="seg-mangueiras"
              type="date"
              max={hojeISO()}
              value={dados.mangueiras_ultima_manutencao ?? ''}
              onChange={(e) => alterar('mangueiras_ultima_manutencao', e.target.value || null)}
              className={CLASSE_CAMPO}
            />
          </div>
        </div>
        <p className="text-xs text-foreground-500 mt-3">
          As datas ficam registradas, mas ainda não geram alerta: a periodicidade de recarga e de
          teste hidrostático não está definida no produto.
        </p>
      </div>

      {/* Observações */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
        <label htmlFor="seg-obs" className="block text-sm font-semibold text-foreground-900 mb-3">
          Observações
        </label>
        <textarea
          id="seg-obs"
          rows={3}
          maxLength={2000}
          value={dados.observacoes ?? ''}
          onChange={(e) => alterar('observacoes', e.target.value)}
          className={`${CLASSE_CAMPO} resize-y`}
        />
      </div>

      <div className="flex items-center justify-end gap-3">
        {aviso && (
          <span
            className={`text-sm flex items-center gap-1.5 ${aviso.tipo === 'ok' ? 'text-green-700' : 'text-red-700'}`}
          >
            <i className={aviso.tipo === 'ok' ? 'ri-check-line' : 'ri-error-warning-line'}></i>
            {aviso.texto}
          </span>
        )}
        <button
          type="button"
          onClick={salvar}
          disabled={salvando}
          className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {salvando ? 'Salvando...' : 'Salvar'}
        </button>
      </div>
    </div>
  );
}
